import React, { useContext } from "react";
import { NavLink } from "react-router-dom";
import classNames from "classnames";
import AuthContext from "src/context/AuthContext";
import DatasetContext from "src/context/DatasetContext";
import { AdminPermissions, ClientPermissions } from "src/common/Permissions";
import { NeurapulseApiKeyButton } from "src/components/neurapulse/NeurapulseApiKeyButton";
import ClientDatasetAPI from "src/clientsiteresults/ClientDatasetAPI";
import OrganizationAPI from "src/admin/Organization/API/OrganizationAPI";
import clsx from "clsx";
import styles from "./ClientsiteNavBar.module.css";

interface IProps {
	renderCoreTesterNavItems: any;
	shouldDisplayDatasetName: any;
	extractParamsFromUrl: any;
	location: any;
}

const ClientsiteNavBar = ({
	renderCoreTesterNavItems,
	shouldDisplayDatasetName,
	extractParamsFromUrl,
	location,
}: IProps) => {
	const [currentDataset, setCurrentDataset] = React.useState<DatasetDTO | null>(
		null
	);
	const [organizationId, setOrganizationId] = React.useState<number | null>(
		null
	);
	const [datasetId, setDatasetId] = React.useState<number | null>(null);
	const [currentOrganization, setCurrentOrganization] =
		React.useState<OrganizationDTO | null>(null);
	const trainingText = <span style={{ color: "#6c9899" }}>- TRAINING</span>;
	const { currentUser } = useContext(AuthContext);
	const { updateCurrentDataset } = useContext(DatasetContext);
	const isExcelerate =
		currentDataset != null && currentDataset.productId === "ExceleRATEV2";
	const [permissions, setPermissions] = React.useState<string[]>([]);

	React.useEffect(() => {
		const { organizationId: orgId, datasetId: dsId } = extractParamsFromUrl(
			location.pathname
		);
		setOrganizationId(Number(orgId));
		setDatasetId(Number(dsId));
	}, [location.pathname, extractParamsFromUrl]);

	React.useEffect(() => {
		if (!currentUser || !currentUser.datasetPermissions || !datasetId) {
			setPermissions([]);
			return;
		}

		const datasetPermissionsDTO = currentUser.datasetPermissions.find(
			(perm) => perm.datasetId === datasetId
		);
		const perms = datasetPermissionsDTO
			? datasetPermissionsDTO.permissions || []
			: [];
		if (currentUser.permissions) {
			perms.push(...currentUser.permissions);
		}
		setPermissions(perms);
	}, [currentUser, datasetId]);

	React.useEffect(() => {
		if (datasetId && organizationId) {
			Promise.all([
				ClientDatasetAPI.getDatset({
					organizationId: organizationId,
					datasetId: datasetId,
				}),
				OrganizationAPI.getClientSiteOrganization({
					organizationId,
				}),
			]).then(([dataset, organization]) => {
				setCurrentDataset(dataset);
				setCurrentOrganization(organization);
			});
		} else {
			setCurrentDataset(null);
			setCurrentOrganization(null);
		}
	}, [datasetId, organizationId]);

	const onRefreshDataset = async () => {
		if (datasetId && organizationId) {
			const ds = await ClientDatasetAPI.getDatset({
				organizationId: organizationId,
				datasetId: datasetId,
			});
			setCurrentDataset(ds);

			// Refresh Results
			await updateCurrentDataset(ds);
		}
	};

	const isNeurapulseAdmin =
		permissions.includes(ClientPermissions.NeurapulseAdmin) ||
		permissions.includes(AdminPermissions.GlobalAdmin);

	const shouldDisableApiKeyButton = !isNeurapulseAdmin;

	const linkTo =
		currentUser &&
		currentUser.permissions!.includes(AdminPermissions.GlobalAdmin)
			? `/organizations/${organizationId}/datasets/admin`
			: `/organizations/${organizationId}/datasets`;
	return (
		<nav className="d-flex flex-row subnavbar navbar-light no-print">
			<ul className={classNames("navbar-nav flex-row")}>
				{currentOrganization && (
					<li className="nav-item">
						<NavLink to={linkTo} className="nav-link">
							<span className="font-weight-bold">
								{currentOrganization.name}
							</span>
						</NavLink>
					</li>
				)}
				{renderCoreTesterNavItems()}
			</ul>
			<ul className={classNames("navbar-nav flex-row ml-lg-auto")}>
				{isExcelerate && (
					<li
						className={clsx("nav-link", {
							[styles.disabledNavItem]: shouldDisableApiKeyButton,
						})}
					>
						<NeurapulseApiKeyButton
							dataset={currentDataset}
							refreshDataset={onRefreshDataset}
							disabled={shouldDisableApiKeyButton}
						/>
					</li>
				)}
				{currentDataset && shouldDisplayDatasetName() && (
					<li className="nav-link font-weight-bold no-pointer">
						Dataset:&nbsp;
						{currentDataset.training && currentDataset.name ? (
							<span>
								{currentDataset.name} {trainingText}
							</span>
						) : (
							currentDataset.name || localStorage.getItem("currentDatasetName")
						)}
					</li>
				)}
			</ul>
		</nav>
	);
};

export default ClientsiteNavBar;
